import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ApiHttpError } from '@/api/auth'

const DEBOUNCE_MS = 3000
const recentErrors = new Map<string, number>()

function shouldShow(message: string): boolean {
  const now = Date.now()
  const key = message.toLowerCase().trim()

  // Cleanup vieux timestamps
  if (recentErrors.size > 20) {
    for (const [k, ts] of recentErrors.entries()) {
      if (now - ts > 10_000) recentErrors.delete(k)
    }
  }

  const last = recentErrors.get(key)
  if (last && now - last < DEBOUNCE_MS) return false
  recentErrors.set(key, now)
  return true
}

interface UseApiErrorOptions {
  onError?: (error: unknown) => void
}

/**
 * Hook utilitaire pour afficher des erreurs API en toast (sonner) avec déduplication.
 *
 * - Toast traduit si la clé i18n existe
 * - Déduplication 3s pour éviter les spams
 * - Compatible avec ApiHttpError typé (statut + data)
 *
 * Pour les redirects login/refresh, c'est l'interceptor Axios qui gère (cf. lib/api.ts).
 */
export function useApiError(options: UseApiErrorOptions = {}) {
  const { t } = useTranslation()
  const optionsRef = useRef(options)
  optionsRef.current = options

  const extractMessage = useCallback(
    (error: unknown): string => {
      // Le corps de la réponse : ApiHttpError le porte en `data`, une erreur
      // axios en `response.data`. Sans la seconde lecture, l'écran affichait
      // « Request failed with status code 400 » au lieu du message du serveur.
      const axios = error as { response?: { status?: number; data?: unknown } } | null
      const statut = error instanceof ApiHttpError ? error.status : axios?.response?.status
      const brut = error instanceof ApiHttpError ? error.data : axios?.response?.data
      if (statut !== undefined) {
        const data = (brut && typeof brut === 'object' ? brut : null) as
          | { error?: string; detail?: string; message?: string; plan_required?: string }
          | null
        if (data?.error === 'plan_required' && data.plan_required) {
          // Verrou de plan (droits_plan.py) : on nomme le plan qui l'inclut.
          const plan = data.plan_required === 'business' ? 'Business' : 'Team'
          return t('plan.required', { plan })
        }
        const message = data?.error || data?.detail || data?.message
        if (typeof message === 'string' && message) return message
        if (error instanceof ApiHttpError) return `HTTP ${statut}`
      }
      if (error instanceof Error) return error.message
      return 'Une erreur est survenue'
    },
    [t],
  )

  const showError = useCallback(
    (messageOrKey: string, title?: string) => {
      let message = messageOrKey
      try {
        const translated = t(messageOrKey)
        if (translated && translated !== messageOrKey) message = translated
      } catch {
        // garder le message original
      }
      if (!shouldShow(message)) return
      toast.error(title || t('common.error') || 'Erreur', { description: message })
    },
    [t],
  )

  const handleError = useCallback(
    (error: unknown) => {
      const message = extractMessage(error)
      if (!shouldShow(message)) return
      toast.error(t('common.error') || 'Erreur', { description: message })
      optionsRef.current.onError?.(error)
    },
    [t, extractMessage],
  )

  const safeCall = useCallback(
    async <T>(
      apiCall: () => Promise<T>,
    ): Promise<{ data?: T; error?: unknown }> => {
      try {
        return { data: await apiCall() }
      } catch (error) {
        handleError(error)
        return { error }
      }
    },
    [handleError],
  )

  return { handleError, showError, safeCall, extractMessage }
}
